import { useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import getLatLng from "@components/GoogleMap/_lib/getLatLng";

const useMarkersClusterer = ({
  map,
  data,
  clustererOptions = {
    radius: 200,
    maxZoom: 17,
  },
  clustererIcon,
  mapMarkerIcon,
}) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const markersLibrary = useMapsLibrary("marker");

  const clustererRef = useRef(null);
  const markersRef = useRef(new Map());
  const markerContentCache = useRef(new Map());

  const handleMarkerSelect = useCallback(
    ({ markerInstance, marker }) => {
      map.panTo(getLatLng(marker));

      setSelectedMarker({
        markerInstance: markerInstance || markersRef.current.get(marker.id),
        marker,
      });
    },
    [map]
  );

  const onMarkerDeselect = useCallback(() => {
    setSelectedMarker(null);
  }, []);

  const renderMarkerIcon = useCallback(
    (item) => {
      if (markerContentCache.current.has(item.id)) {
        return markerContentCache.current.get(item.id);
      }

      const MapMarkerComponent = memo(mapMarkerIcon);

      const container = document.createElement("div");
      ReactDOM.render(
        <MapMarkerComponent
          marker={{ provider: item.provider, status: item.status }}
        />,
        container
      );

      markerContentCache.current.set(item.id, container);

      return container;
    },
    [mapMarkerIcon]
  );

  useEffect(() => {
    if (!map || !markersLibrary || !data?.length) {
      return;
    }

    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({
        map,
        markers: [],
        algorithm: new SuperClusterAlgorithm(clustererOptions),
        renderer: clustererIcon({ markersLibrary }),
      });
    }

    const markersToAdd = [];
    const markersToRemove = [];

    // ADD
    data.forEach((item) => {
      const currentMarkerInstance = markersRef.current.get(item.id);

      if (!currentMarkerInstance) {
        const marker = new markersLibrary.AdvancedMarkerElement({
          map,
          position: getLatLng(item),
          content: renderMarkerIcon(item),
          collisionBehavior: "OPTIONAL_AND_HIDES_LOWER_PRIORITY",
        });

        marker.addListener("gmp-click", () =>
          handleMarkerSelect({ markerInstance: marker, marker: item })
        );

        markersRef.current.set(item.id, marker);
        markersToAdd.push(marker);
      } else {
        markersToAdd.push(currentMarkerInstance);
      }
    });

    // REMOVE
    markersRef.current.forEach((marker, id) => {
      if (
        !data.find((item) => item.id === id)
        // !(selectedMarker?.id === id) // do not remove marker if it's currently selected
      ) {
        markersToRemove.push(marker);
      }
    });

    if (markersToRemove.length) {
      clustererRef.current.removeMarkers(markersToRemove, true);
    }

    if (markersToAdd.length) {
      clustererRef.current.addMarkers(markersToAdd, true);
    }

    clustererRef.current.render();
  }, [data, map, markersLibrary]);

  useEffect(
    () => () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }

      if (markersRef.current) {
        markersRef.current.forEach((marker) => {
          marker.map = null;
        });

        markersRef.current = new Map();
      }
    },
    []
  );

  return {
    selectedMarker,
    onMarkerDeselect,
    onMarkerSelect: handleMarkerSelect,
  };
};

export default useMarkersClusterer;
